import React, { FC, useEffect, useMemo, useState } from 'react';
import { getParentEventDetails, switchDailyRequiredEvent, updateChildEventTitle, updateOptionTitle } from '../../../../../api/group.api';
import { createNotification } from '../../../../../utilities/helper/notification.helper';
import { HIDE_CHILD_EVENTS, MESSAGE_TYPE } from '../../../../../common/constants/common.constants';
import { Box, Checkbox, CircularProgress, Icon, Typography } from '@mui/material';
import { centeredContainerStyle } from '../../LocationsAndGroupsTab/LocationsAndGroupsTab.styles';
import { ActionMode, EventTitleEnum, LanguageCodeEnum } from '../../../../../common/enums/common.enum';
import { BasicEventConfig } from '../../../../../common/interfaces/event.interface';
import { ReactComponent as EditIcon } from '../../../../../assets/edit-icon.svg';
import EditTitleModal from '../EditTitleModal/EditTitleModal';
import GenericInfoModal from '../../../../../components/GenericInfoModal';
import ToiletOptions from '../../../../../components/ToiletOptions/ToiletOptions';
import { EventDetailsBox, EventHeaderBox, EventPaper } from './EventDetails.styles';
import LanguageSelector from '../../../../../components/LanguageSelector/LanguageSelector';
import { getEventItemsForCount } from '../../../../../utilities/helper/eventCount.helper';
import { LocalizedTitle } from '../../../../../utilities/helper/language.helper';

const STORAGE = process.env.REACT_APP_STORAGE as string;

interface EventDetailsProps {
  eventId?: string;
  groupId?: string;
  eventName?: EventTitleEnum | LocalizedTitle;
  eventDetailsMap?: Partial<Record<EventTitleEnum, BasicEventConfig>>;
  mode?: ActionMode;
}

interface EditableItem {
  id: string;
  title: LocalizedTitle;
  type: 'option' | 'childEvent';
}

export interface UpdatedTitle {
  eng: string;
  secondary: { lang: LanguageCodeEnum; value: string };
}

const EventDetails: FC<EventDetailsProps> = ({ eventId, groupId, eventName, eventDetailsMap, mode }) => {
  const [eventDetails, setEventDetails] = useState<BasicEventConfig>();
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [pendingDailyRequiredStatus, setPendingDailyRequiredStatus] = useState<boolean | null>(null);
  const [selectedItem, setSelectedItem] = useState<EditableItem | null>(null);
  const [secondaryLang, setSecondaryLang] = useState<LanguageCodeEnum>(LanguageCodeEnum.SRB);

  useEffect(() => {
    if (groupId && eventId) {
      loadGroupEvents();
    } else if (eventName && eventDetailsMap) {
      const details = eventDetailsMap[eventName as unknown as EventTitleEnum];
      if (details) {
        setEventDetails(details);
      }
    }
  }, [groupId, eventId, eventName, eventDetailsMap, secondaryLang]);

  const allItems = eventDetails
    ? getEventItemsForCount(eventDetails.title as EventTitleEnum, eventDetails.childEvents ?? [], eventDetails.options ?? [])
    : [];

  const loadGroupEvents = async () => {
    try {
      setLoading(true);
      if (groupId && eventId) {
        const response = await getParentEventDetails(groupId, eventId);
        if (response.results) {
          setEventDetails(response.results);
        }
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTitle = async (updated: UpdatedTitle) => {
    if (!groupId || !selectedItem) return;
    const payload: LocalizedTitle = {
      eng: updated.eng
    };
    if (updated.secondary) {
      payload[updated.secondary.lang] = updated.secondary.value;
    }
    try {
      if (selectedItem?.type === 'childEvent') {
        await updateChildEventTitle(selectedItem.id, groupId, payload, secondaryLang);
        setEventDetails(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            childEvents: prev.childEvents.map(child =>
              child.id === selectedItem.id
                ? {
                    ...child,
                    title: {
                      ...child.title,
                      eng: updated.eng,
                      ...(updated.secondary ? { [updated.secondary.lang]: updated.secondary.value } : {})
                    }
                  }
                : child
            )
          };
        });
      } else if (selectedItem.type === 'option') {
        await updateOptionTitle(selectedItem.id, groupId, payload, secondaryLang);
        setEventDetails(prev =>
          prev
            ? {
                ...prev,
                options: prev.options.map(opt =>
                  opt.id === selectedItem.id
                    ? {
                        ...opt,
                        title: {
                          ...opt.title,
                          eng: updated.eng,
                          ...(updated.secondary ? { [updated.secondary.lang]: updated.secondary.value } : {})
                        }
                      }
                    : opt
                )
              }
            : prev
        );
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to update title');
    }
  };

  const switchDailyRequiredStatus = async (newChecked: boolean) => {
    if (!groupId || !eventId) return;
    try {
      await switchDailyRequiredEvent(eventId, groupId);
      setEventDetails(prev => (prev ? { ...prev, isDailyRequired: newChecked } : prev));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to update daily required status');
    }
  };
  const handleEditOptionClick = (option: { id?: string; title: LocalizedTitle }) => {
    if (!option.id) return;
    setSelectedItem({ id: option.id, title: option.title, type: 'option' });
    setOpenEditModal(true);
  };

  const handleEditChildEventClick = (child: { id?: string; title: LocalizedTitle }) => {
    if (!child.id) return;
    setSelectedItem({ id: child.id, title: child.title, type: 'childEvent' });
    setOpenEditModal(true);
  };
  const handleDailyRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked;
    setPendingDailyRequiredStatus(newChecked);
    setOpenInfoModal(true);
  };

  const shouldShowChildEvents = useMemo(() => {
    if (!eventDetails?.childEvents?.length || !eventDetails.title) return false;

    return eventDetails.title && !HIDE_CHILD_EVENTS.includes(eventDetails.title);
  }, [eventDetails]);

  if (loading) {
    return (
      <Box sx={centeredContainerStyle}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <EventPaper>
      <Box px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
        {mode === ActionMode.EDIT ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox size="small" checked={eventDetails?.isDailyRequired} onChange={handleDailyRequiredChange} />
            <Typography variant="h5" fontWeight={400} color="text.primary">
              Mandatory for daily report
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14 }}>
            This main event is
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
              {!eventDetails?.isDailyRequired ? ' not ' : ' '}
              daily required
            </Typography>
          </Typography>
        )}
        <LanguageSelector value={secondaryLang} onChange={setSecondaryLang} items={allItems} />
      </Box>

      {shouldShowChildEvents && (
        <>
          <EventHeaderBox>
            <Typography variant="caption">Child events:</Typography>
          </EventHeaderBox>
          <Box px={2} py={1} display="flex" flexDirection="column" gap={1.5}>
            {eventDetails?.childEvents.map((child, idx) => (
              <EventDetailsBox
                key={idx}
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {eventDetails.title === EventTitleEnum.TOILET && child.icon && (
                    <img src={`${STORAGE}${child.icon}`} style={{ width: 20 }} alt="Option icon" />
                  )}
                  <Typography variant="h5">{child.title.eng}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} sx={{ lineHeight: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      fontStyle: !child.title[secondaryLang] ? 'italic' : 'normal',
                      color: !child.title[secondaryLang] ? '#D9D9D9' : 'text.primary'
                    }}
                  >
                    {child.title[secondaryLang] ? child.title[secondaryLang] : 'No translation'}
                  </Typography>
                  {mode === ActionMode.EDIT && (
                    <Icon sx={{ cursor: 'pointer' }} onClick={() => handleEditChildEventClick({ id: child.id, title: child.title })}>
                      <EditIcon />
                    </Icon>
                  )}
                </Box>
              </EventDetailsBox>
            ))}
          </Box>
        </>
      )}

      <EventHeaderBox>
        <Typography variant="caption">Options:</Typography>
      </EventHeaderBox>
      <Box px={2} py={1} display="flex" flexDirection="column" gap={1.5}>
        {eventDetails?.title !== EventTitleEnum.TOILET ? (
          eventDetails?.options.map((opt, idx) => (
            <EventDetailsBox key={idx}>
              <Box display="flex" alignItems="center" gap={1}>
                {opt.icon && <img src={`${STORAGE}${opt.icon}`} style={{ width: 20 }} alt="Option icon" />}
                <Typography variant="h5">{opt.title.eng}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ lineHeight: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    fontStyle: !opt.title[secondaryLang] ? 'italic' : 'normal',
                    color: !opt.title[secondaryLang] ? '#D9D9D9' : 'text.primary'
                  }}
                >
                  {opt.title[secondaryLang] ? opt.title[secondaryLang] : 'No translation'}
                </Typography>
                {mode === ActionMode.EDIT && (
                  <Icon sx={{ cursor: 'pointer' }} onClick={() => handleEditOptionClick({ id: opt.id, title: opt.title })}>
                    <EditIcon />
                  </Icon>
                )}
              </Box>
            </EventDetailsBox>
          ))
        ) : (
          <ToiletOptions showCaption={false} />
        )}
      </Box>
      {selectedItem && (
        <EditTitleModal
          open={openEditModal}
          type={selectedItem.type}
          option={selectedItem ? selectedItem.title : undefined}
          secondaryLang={secondaryLang}
          onClose={() => setOpenEditModal(false)}
          onSave={handleSaveTitle}
        />
      )}
      <GenericInfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        label={`${eventDetails?.title ?? ''} - mandatory for daily report`}
        message={`Are you sure you want to ${pendingDailyRequiredStatus ? 'select' : 'deselect'} this option?`}
        onConfirm={() => {
          if (pendingDailyRequiredStatus !== null) {
            switchDailyRequiredStatus(pendingDailyRequiredStatus);
            setPendingDailyRequiredStatus(null);
          }
          setOpenInfoModal(false);
        }}
        confirmLabel={pendingDailyRequiredStatus ? 'Select' : 'Deselect'}
        confirmColor="#3F3273"
      />
    </EventPaper>
  );
};
export default EventDetails;
